import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { OfferSource } from '@cds';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { SitecoreEventDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { produce } from 'immer';
import { clone, flatten } from 'lodash-es';
import { skip } from 'rxjs';

import { EpcotConfigService } from '../../../common/epcot-config.service';
import { BaseTreeItem, LeftNavType, TreeItem, TreeItemType } from '../../../event-details-common/event-details-common.models';
import { getLeagueId } from '../../../event-details-common/event-details-common.utils';
import { TreeScrollDirective } from '../../../tree/tree-scroll.directive';
import { LiveEventsNavigationSubscriptionService } from '../../live-events-navigation/live-events-navigation-subscription.service';
import { EventSwitcherTrackingService } from '../event-switcher-tracking.service';
import { EventSwitcherToggleService } from '../services/event-switcher-toggle.service';

@Component({
    selector: 'ms-event-switcher-navigation',
    templateUrl: './event-switcher-navigation.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class EventSwitcherNavigationComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() tree: BaseTreeItem;
    @Input() event: EventModel;
    @Input() showHeader: boolean;

    @ViewChild(TreeScrollDirective, { static: true }) treeScroll: TreeScrollDirective;

    private readonly _doc = inject(DOCUMENT);
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private subscriptionsService: LiveEventsNavigationSubscriptionService,
        private sitecoreEventDetails: SitecoreEventDetailsConfig,
        private trackingService: EventSwitcherTrackingService,
        private toggleService: EventSwitcherToggleService,
        private epcotConfigService: EpcotConfigService,
        private routerEventsService: RouterEventsService,
    ) {}

    ngOnInit(): void {
        this.subscribeEvents();
        this.subscriptionsService.updatedEvents$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((events) => this.updateEvents(events));
        this.routerEventsService.currentActivationEnd.pipe(skip(1), takeUntilDestroyed(this.destroyRef)).subscribe((_) => this.close());
    }

    ngAfterViewInit(): void {
        if (!this.epcotConfigService.isEnabled()) {
            const headerElement = this._doc.querySelector<HTMLElement>(`.slot-header`);

            if (headerElement) {
                this._doc
                    .querySelectorAll<HTMLElement>('.event-switcher-dialog')!
                    .forEach((s) => (s.style.height = `calc(100% - ${headerElement.clientHeight}px)`));
            }
        }

        this.treeScroll.scrollToCenterOrTop(this.event);
    }

    ngOnDestroy(): void {
        this.subscriptionsService.unsubscribeAll();
    }

    toggleItem(item: TreeItem): void {
        if (item.type === TreeItemType.League) {
            this.toggleLeague(item);
        } else {
            return;
        }

        this.changeDetectorRef.markForCheck();
    }

    onSelectEvent(selectedEvent: EventModel): void {
        this.trackingService.trackEventSelect(this.event.id, selectedEvent.id, selectedEvent.name, selectedEvent.live);
    }

    close(): void {
        this.toggleService.close();
        this.trackingService.trackClose(this.event.id, this.event.live);
    }

    get title(): string {
        return this.event.live
            ? this.sitecoreEventDetails.eventDetails.EventSwitcherLiveTitle
            : this.sitecoreEventDetails.eventDetails.EventSwitcherPrematchTitle;
    }

    private toggleLeague(item: TreeItem): void {
        this.tree = produce(this.tree, (draft: BaseTreeItem) => {
            const league = draft.children.find((l) => l.id === item.id)!;
            league.expanded = !league.expanded;
            league.events.forEach((e) => (e.hidden = !e.hidden));
        });
    }

    private subscribeEvents(): void {
        const events = flatten([...this.tree.children[0].events, ...this.tree.children[0].children.map((league) => league.events)])
            .map((e) => e.data)
            .filter((event) => event.offerSource === OfferSource.V2 || event.live);

        this.subscriptionsService.subscribe(events);
    }

    private updateEvents(events: EventModel[]): void {
        this.tree = produce(this.tree, (draft: BaseTreeItem) => events.forEach((event) => this.findAndUpdateEvent(event, draft.children[0])));

        this.changeDetectorRef.markForCheck();
    }

    private findAndUpdateEvent(event: EventModel, draft: TreeItem): void {
        const league = draft.leftNavType === LeftNavType.Competition ? draft : draft.children.find((l) => l.id === getLeagueId(event));

        const oldEventIndex = league?.events.findIndex((e) => e.data.id === event.id) ?? -1;
        if (oldEventIndex >= 0) {
            league!.events[oldEventIndex].data = clone(event);
        }
    }
}
